import { isEqual } from 'lodash-es';
import { useSearchParams } from 'react-router';

import { useAppSelector } from 'src/app/store/hooks';
import { RootState } from 'src/app/store/types';
import { usePaginationState } from 'src/common/DataGrid/usePaginationState';
import {
  TripListRead,
  TripsListApiArg,
  useTripsListQuery,
} from 'src/common/external/bambi-api/bambiApi';

import { InvoicedTripsValue } from '../../constants';

export function usePaginatedTripsListQuery() {
  const filterParamsFromState = useAppSelector(
    selectFilterParamsFromState,
    isEqual
  );

  const ordering = useSearchParams()[0].get(
    'ordering'
  ) as TripsListApiArg['ordering'];

  const { pagination, handlePaginationChange, paramsWithPage } =
    usePaginationState<TripsListApiArg>({
      ...filterParamsFromState,
      ordering,
    });

  const tripsListQueryState = useTripsListQuery(paramsWithPage, {
    selectFromResult: (result) => {
      return {
        ...result,
        trips: result.data?.results || ([] as TripListRead[]),
        count: result.data?.count || 0,
      };
    },
  });

  return {
    tripsListQueryState,
    pagination,
    handlePaginationChange,
  };
}

function getHasInvoiceValue(filterInvoicedTrips?: InvoicedTripsValue) {
  switch (filterInvoicedTrips) {
    case InvoicedTripsValue.ALL_TRIPS:
      return undefined;
    case InvoicedTripsValue.INVOICED_TRIPS:
      return true;
    case InvoicedTripsValue.NOT_INVOICED_TRIPS:
      return false;
    case InvoicedTripsValue.PAID_INVOICE_TRIPS:
      return undefined;
  }
}

function getHasPaidInvoiceValue(filterInvoicedTrips?: InvoicedTripsValue) {
  if (filterInvoicedTrips === InvoicedTripsValue.PAID_INVOICE_TRIPS) {
    return true;
  }
  return undefined;
}

function selectFilterParamsFromState(state: RootState) {
  return {
    search: state.trip.tripSearchTerm,
    rangeStart: state.trip.selectedDateRange?.from || '',
    rangeEnd: state.trip.selectedDateRange?.to || '',
    createdAtRangeStart: state.trip.selectedCreatedAtDateRange?.from || '',
    createdAtRangeEnd: state.trip.selectedCreatedAtDateRange?.to || '',
    payerIn: state.trip.selectedPayers,
    statusIn: state.trip.selectedStatuses,
    driverIn: state.trip.selectedDrivers,
    tags: state.trip.selectedTags.join(','),
    hasInvoice: getHasInvoiceValue(state.trip.filterInvoicedTrips),
    hasPaidInvoice: getHasPaidInvoiceValue(state.trip.filterInvoicedTrips),
    vehicles: state.trip.filterVehicles.join(','),
    priceMax: state.trip.filterPrice?.max,
    priceMin: state.trip.filterPrice?.min,
    hasDriverEnteredNotes: state.trip.filterIncludeNote,
    spaceType: state.trip.filterSpaceTypes.join(','),
    canceledReasonIn: state.trip.filterCanceledReasons,
  };
}
